import React, { createContext, useContext, useRef, useState } from "react";
import { GridDetailRowProps } from "@progress/kendo-react-grid";
import { Button, ColumnFitColumn, Icon, toast, useColumnFit, useMedia } from "@axum/ax-design";
import { updateOrderItemState } from "../../Dashboard/Tabs/retry/fetchers";
import { FeaturesAllowedName } from "../../../utils/commonFetchers";
import { formatMoney, isBudget, isDownloadPending, isRejectedOrder } from "../../../utils/orderDisplay";
import { SCROLL_TRACK, ScrollHint, ScrollMoreButtons, ScrollTracks, useSyncedScroll } from "../ScrollHint";
import { SPLIT_MEDIA } from "./styles";
import { ConfirmRequest } from "./useConfirm";

export interface OrderBubbleActions {
  featuresAllowed: string[];
  showLogs: (order: any) => void;
  updateState: (order: any, erpState: string) => Promise<void>;
  confirm: (request: ConfirmRequest) => void;
}

export const OrderBubbleContext = createContext<OrderBubbleActions>({
  featuresAllowed: [],
  showLogs: () => undefined,
  updateState: () => Promise.resolve(),
  confirm: () => undefined,
});

const ITEM_COLUMNS: ColumnFitColumn[] = [
  { key: "articleId", weight: 3, min: 4 },
  { key: "articleName", weight: 20, min: 10 },
  { key: "bundles", weight: 2, min: 3.25 },
  { key: "units", weight: 2, min: 2.75 },
  { key: "noFeeBundles", weight: 2, min: 5 },
  { key: "noFeeUnits", weight: 2, min: 4.5 },
  { key: "totalGrossPrice", weight: 4, min: 7 },
  { key: "totalBonusAndDiscount", weight: 2, min: 3.75 },
  { key: "totalNetPrice", weight: 4, min: 7 },
  { key: "totalTaxes", weight: 4, min: 7 },
  { key: "totalFinalPrice", weight: 4, min: 7.25 },
];

const VOID_COLUMNS: ColumnFitColumn[] = [...ITEM_COLUMNS, { key: "void", weight: 0, min: 7.5 }];

const FLEX_COLUMN = "articleName";

const operationName = (order: any) => (isBudget(order) ? "presupuesto" : "pedido");

const Meta = ({ label, value, className }: { label: string; value: React.ReactNode; className?: string }) => (value ? (
  <span className={`ax-detail-meta${className ? ` ${className}` : ""}`}>
    <span className="ax-detail-label">{label}</span>
    <strong>{value}</strong>
  </span>
) : null);

const FooterFigure = ({ label, value }: { label: string; value: number }) => (
  <span>
    <span className="ax-detail-footer-label">{label}</span> <strong className="ax-font-tabular">{formatMoney(value)}</strong>
  </span>
);

interface ItemField {
  key: string;
  label: string;
  value: (item: any) => React.ReactNode;
  money?: boolean;
}

const ITEM_FIELDS: ItemField[] = [
  { key: "articleId", label: "Código", value: item => item.articleId },
  { key: "bundles", label: "Bultos", value: item => item.bundles },
  { key: "units", label: "Unid.", value: item => item.units },
  { key: "noFeeBundles", label: "Bultos s/c", value: item => item.noFeeBundles },
  { key: "noFeeUnits", label: "Unid. s/c", value: item => item.noFeeUnits },
  { key: "totalGrossPrice", label: "Bruto", value: item => formatMoney(item.totalGrossPrice), money: true },
  { key: "totalBonusAndDiscount", label: "Bonif.", value: item => `${item.totalBonusAndDiscount} %` },
  { key: "totalNetPrice", label: "Neto", value: item => formatMoney(item.totalNetPrice), money: true },
  { key: "totalTaxes", label: "Impuestos", value: item => formatMoney(item.totalTaxes), money: true },
];

interface ItemProps {
  item: any;
  order: any;
  canVoid: boolean;
}

const useItemVoid = (item: any, order: any) => {
  const [voided, setVoided] = useState<boolean>(Boolean(item.deleted));
  const { confirm } = useContext(OrderBubbleContext);

  const toggleVoid = () => updateOrderItemState(voided, item.id).then(x => {
    if (x.notUpdated > 0) {
      toast.show(x.error, { type: "danger" });
      return;
    }
    item.deleted = !voided;
    setVoided(!voided);
  });

  const askVoid = () => confirm({
    title: "¿Anular el artículo?",
    description: <><strong>{item.articleName}</strong> queda anulado en el {operationName(order)} {order.id}. Lo podés desanular después.</>,
    confirmLabel: "Anular artículo",
    tone: "danger",
    run: toggleVoid,
  });

  const button = (
    <Button size="sm" variant={voided ? "ghost" : "ghost-danger"} onClick={voided ? toggleVoid : askVoid}>
      {voided ? "Desanular" : "Anular"}
    </Button>
  );

  return { struck: voided ? "ax-text-muted ax-text-decoration-line-through" : "", button };
};

const ItemRow = ({ item, order, canVoid }: ItemProps) => {
  const { struck, button } = useItemVoid(item, order);
  return (
    <tr className={struck || undefined}>
      <td className="ax-font-tabular ax-datagrid-table-only">{item.articleId}</td>
      <td className="ax-text-start">{item.articleName}</td>
      <td className="ax-font-tabular">{item.bundles}</td>
      <td className="ax-font-tabular">{item.units}</td>
      <td className="ax-font-tabular ax-datagrid-table-only">{item.noFeeBundles}</td>
      <td className="ax-font-tabular ax-datagrid-table-only">{item.noFeeUnits}</td>
      <td className="ax-font-tabular ax-datagrid-table-only">{formatMoney(item.totalGrossPrice)}</td>
      <td className="ax-font-tabular ax-datagrid-table-only">{`${item.totalBonusAndDiscount} %`}</td>
      <td className="ax-font-tabular ax-datagrid-table-only">{formatMoney(item.totalNetPrice)}</td>
      <td className="ax-font-tabular ax-datagrid-table-only">{formatMoney(item.totalTaxes)}</td>
      <td className="ax-font-tabular ax-fw-medium">{formatMoney(item.totalFinalPrice)}</td>
      {canVoid && <td>{button}</td>}
    </tr>
  );
};

const ItemCard = ({ item, order, canVoid, hint }: ItemProps & { hint: ScrollTracks }) => {
  const { struck, button } = useItemVoid(item, order);
  const mark = (className: string) => `${className} ${struck}`.trim();
  return (
    <li className="o360-item">
      <span className="o360-item-head">
        <span className={mark("o360-item-name")}>{item.articleName}</span>
        <span className={mark("ax-font-tabular ax-fw-medium")}>{formatMoney(item.totalFinalPrice)}</span>
        {canVoid && button}
      </span>
      <ScrollHint className={hint.className}>
        <span className={mark(`${SCROLL_TRACK} o360-item-fields`)}>
          {ITEM_FIELDS.map(field => (
            <span key={field.key} className={`o360-item-field${field.money ? " is-money" : ""}`}>
              <span className="o360-item-label">{field.label}</span>
              <span className="ax-font-tabular">{field.value(item)}</span>
            </span>
          ))}
        </span>
        <ScrollMoreButtons hint={hint} previous="Ver datos anteriores" next="Ver más datos" />
      </ScrollHint>
    </li>
  );
};

export default (props: GridDetailRowProps) => {
  const order = props.dataItem;
  const actions = useContext(OrderBubbleContext);
  const rejected = isRejectedOrder(order);
  const canRetry = rejected && actions.featuresAllowed.includes(FeaturesAllowedName.REJECTED_ORDER);
  const canVoid = rejected || (isDownloadPending(order) && actions.featuresAllowed.includes(FeaturesAllowedName.BUDGETS));
  const items: any[] = order.detail || [];
  const body = useRef<HTMLDivElement>(null);
  const columns = canVoid ? VOID_COLUMNS : ITEM_COLUMNS;
  const fit = useColumnFit(body, { columns });
  const cards = useMedia(SPLIT_MEDIA);
  const hint = useSyncedScroll(body);

  const askCancel = () => actions.confirm({
    title: `¿Cancelar el ${operationName(order)} ${order.id}?`,
    description: "Pasa a Cancelado y ya no se puede reintentar el envío.",
    confirmLabel: `Cancelar ${operationName(order)}`,
    tone: "warning",
    run: () => actions.updateState(order, "CANCELADO"),
  });

  const logsButton = (className: string) => (
    <Button size="sm" variant="ghost" className={className} onClick={() => actions.showLogs(order)}>
      <Icon name="list" />Logs
    </Button>
  );

  return (
    <div className="ax-detail">
      <div className="ax-detail-header">
        <span className="ax-detail-title">
          <span className="ax-detail-icon"><Icon name={isBudget(order) ? "document" : "cart"} /></span>
          {order.axumOperationType}
        </span>
        {logsButton("ax-datagrid-card-only")}
        <span className="o360-detail-metas">
          <Meta label="ID Axum" value={order.id} />
          <Meta label="ID ERP" value={order.erpOrderId} />
          <Meta label="ID Source" value={order.originOrderId} />
          <Meta label="Factura" value={order.invoiceId} />
          <Meta label="Canal" value={order.clientChannel} />
          <Meta label="Artículos" value={String(items.length)} className="ax-datagrid-card-only" />
        </span>
        <span className={`o360-detail-actions${canRetry ? "" : " ax-datagrid-table-only"}`}>
          {logsButton("ax-datagrid-table-only")}
          {canRetry && (
            <>
              <Button size="sm" variant="ghost-warning" onClick={askCancel}>
                Cancelar
              </Button>
              <Button size="sm" variant="primary" onClick={() => actions.updateState(order, "REINTENTANDO ENVIO")}>
                <Icon name="repeat" />Reintentar envío
              </Button>
            </>
          )}
        </span>
      </div>
      <div className="ax-detail-body" ref={body}>
        {cards ? (
          <ul className="o360-items">
            {items.map(item => <ItemCard key={item.id} item={item} order={order} canVoid={canVoid} hint={hint} />)}
          </ul>
        ) : (
          <table className="ax-table ax-table-fixed">
            <colgroup>
              {columns.map(column => (
                <col key={column.key} style={column.key === FLEX_COLUMN ? undefined : { width: fit.widths[column.key] }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th className="ax-datagrid-table-only">Código</th>
                <th className="ax-text-start">Artículo</th>
                <th>Bultos</th>
                <th>Unid.</th>
                <th className="ax-datagrid-table-only">Bultos s/c</th>
                <th className="ax-datagrid-table-only">Unid. s/c</th>
                <th className="ax-datagrid-table-only">Bruto</th>
                <th className="ax-datagrid-table-only">Bonif.</th>
                <th className="ax-datagrid-table-only">Neto</th>
                <th className="ax-datagrid-table-only">Impuestos</th>
                <th>Final</th>
                {canVoid && <th aria-label="Acciones" />}
              </tr>
            </thead>
            <tbody>
              {items.map(item => <ItemRow key={item.id} item={item} order={order} canVoid={canVoid} />)}
            </tbody>
          </table>
        )}
      </div>
      <div className="ax-detail-footer">
        <div className="ax-detail-footer-meta">
          <span className="ax-detail-footer-label ax-datagrid-table-only">{items.length} artículos</span>
          <FooterFigure label="Neto" value={order.totalNetPrice} />
          <FooterFigure label="IVA + imp. internos" value={order.totalTaxes} />
          <FooterFigure label="Percepciones" value={order.totalPerceptions} />
        </div>
        <span className="ax-detail-footer-label">Total</span>
        <span className="ax-detail-total">{formatMoney(order.total)}</span>
      </div>
    </div>
  );
};
